import React, {
  HTMLAttributes,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  useWindowDimensions,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  Breadcrumbs,
  ParagraphsRenderer,
  ThemeTags,
  MoreLikeThis,
} from '../../../../components';

import {
  getHexWithOpacity,
  setPrimaryColor,
  setPrimaryColorOpaque,
  setSecondaryColor,
} from '../../../../helpers/colors';
import {
  SCREEN_TABLET,
  DEFAULT_LANGUAGE,
  BLACK,
  WHITE,
} from '../../../../config/constants';

import './style.scss';

interface NodeProjectPageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<NodeProject>;
}

const NodeProjectPage: React.FC<NodeProjectPageProps> = ({ entity }) => {
  const { t } = useTranslation();
  const classes = classNames('Node Node-type-breadcrumbs NodeProjectPage');
  const project = entity.toJS();
  const title = project.title[0].value;
  const labels = project.field_labels;
  const heroImage = project.field_media_item[0];
  const body = project.body[0]?.processed;
  const link = project.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const lang = project.langcode[0].value || DEFAULT_LANGUAGE;
  const themes = project.field_themes;

  const primaryColor = project.field_color_primary[0]?.value || WHITE;
  const secondaryColor = project.field_color_secondary[0]?.value || BLACK;

  const { width } = useWindowDimensions();
  const isMobile = width < SCREEN_TABLET;

  const hero = useRef<HTMLDivElement>(null);
  const opacity = useRef(1);
  const heroHeight = useRef(1000);

  const handleScroll = () => {
    opacity.current = 0.3 + ((heroHeight.current - window.scrollY) / 1000);

    const hexOpaque = getHexWithOpacity(primaryColor, opacity.current);
    setPrimaryColorOpaque(hexOpaque);
  };

  useEffect(() => {
    setPrimaryColor(primaryColor);
    setPrimaryColorOpaque(getHexWithOpacity(primaryColor, 1));
    setSecondaryColor(secondaryColor);
    // comment this line out when on local if
    // working on firefox and it's necessary to view the elements tab in the inspector
    // see "Firefox scrolling performance issues" section in README for explanation
    window.addEventListener('scroll', handleScroll);

    if (hero.current) {
      // @ts-ignore
      const backgroundHeight: number = hero.current.getBoundingClientRect().height;
      heroHeight.current = backgroundHeight > 1000 ? backgroundHeight : 1000;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      setPrimaryColor(WHITE);
      setPrimaryColorOpaque(WHITE);
    };
  });

  return (
    <article
      itemType="http://schema.org/WebPage"
      className={classes}
    >
      <div
        ref={hero}
        className="NodeProjectPage__background"
      >
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <div className="NodeProjectPage__hero">
                <div className="NodeProjectPage__hero__text">
                  <Breadcrumbs
                    parentTitle={t('content_types.project.type')}
                    parentLink={lang === DEFAULT_LANGUAGE ? `/${lang}/werk` : `/${lang}/work`}
                    currentPage={title}
                  />
                  <h1 itemProp="name">{title}</h1>
                  {body && (
                    <WysiwygField
                      property="schema:text"
                      className="text--large"
                      html={body}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
        {isMobile ? (
          <div className="NodeProjectPage__hero__image">
            <DynamicMediaItem
              media={heroImage}
              imageAspectRatio="16_9"
            />
          </div>
        ) : (
          <Container>
            <div className="row">
              <div className="col-12">
                <div className="NodeProjectPage__hero__image">
                  <DynamicMediaItem
                    media={heroImage}
                    imageAspectRatio="16_9"
                  />
                </div>
              </div>
            </div>
          </Container>
        )}
      </div>

      <ParagraphsRenderer
        paragraphs={entity.get('field_paragraphs')}
        exclude={['cta_wrapper']}
      />

      {themes.length > 0 && (
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <ThemeTags
                title={t('content_types.project.themes_title')}
                themes={themes}
                linkUrl={linkUrl}
                linkTitle={linkTitle}
              />
            </div>
          </div>
        </Container>
      )}

      <ParagraphsRenderer
        paragraphs={entity.get('field_paragraphs')}
        only={['cta_wrapper']}
      />

      <MoreLikeThis
        labels={labels}
        themes={themes}
        entity={project}
      />

    </article>
  );
};

export default NodeProjectPage;
